// CMP-0107 · LFS Place Suggest (location box with live suggestions) · Catalog Engine · Lot Discovery · owner: Platform
// 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
// 99-1-decision: ADAPT the Header D search suggestions into one shared combobox (WAI-ARIA combobox pattern) used by the hero and header (registry SEL-LFS-001)
"use client"

import { useRouter } from "next/navigation"
import { useId, useMemo, useState, type CSSProperties } from "react"
import { Input } from "@/components/ui/input"
import { Icon } from "./primitives"
import { matchPlaces, type PlaceHit } from "@/lib/lfs/places"

export function PlaceSuggest({
  places, value, onChange, placeholder, inputStyle, autoFocus, ariaLabel = "Location", dropTop = "calc(100% + 6px)",
}: {
  places: PlaceHit[]
  value: string
  onChange: (v: string) => void
  placeholder: string
  inputStyle?: CSSProperties
  autoFocus?: boolean
  ariaLabel?: string
  dropTop?: string
}) {
  const router = useRouter()
  const id = useId()
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(-1)
  // 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
  // 99-1-decision: the last option is always "Search all lots for <text>", so a nickname or a village not yet
  // grouped as a place (e.g. AAV) still reaches its lots through the alias-aware lot search
  const matched = useMemo(() => matchPlaces(places, value, 7), [places, value])
  const term = value.trim()
  const hits: PlaceHit[] = term.length >= 2
    ? [...matched, { label: `Search all lots for “${term}”`, kind: "Province", sub: "", count: -1, href: `/lots?q=${encodeURIComponent(term)}`, icon: "search" }]
    : matched
  const show = open && hits.length > 0

  const pick = (p: PlaceHit) => {
    setOpen(false)
    if (p.count >= 0) onChange(p.label)
    router.push(p.href)
  }

  return (
    <>
      <Input
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={show}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={show && hi >= 0 ? `${id}-${hi}` : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
          setHi(-1)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!show) return
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % hits.length) }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h <= 0 ? hits.length - 1 : h - 1)) }
          else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(hits[hi]) }
          else if (e.key === "Escape") setOpen(false)
        }}
        style={{ border: 0, boxShadow: "none", outline: 0, font: "inherit", background: "transparent", padding: 0, height: "auto", width: "100%", minWidth: 0, color: "#121722", ...inputStyle }}
      />
      {show && (
        <ul id={`${id}-list`} role="listbox" aria-label="Matching places"
          style={{ position: "absolute", left: 0, right: 0, top: dropTop, zIndex: 40, margin: 0, padding: 6, listStyle: "none", background: "#fff", border: "1px solid #E2E6EC", borderRadius: 14, boxShadow: "0 18px 44px -18px rgba(18,23,34,.45)", maxHeight: 360, overflowY: "auto", color: "#121722", textAlign: "left" }}>
          {hits.map((p, i) => (
            <li key={p.kind + p.label} id={`${id}-${i}`} role="option" aria-selected={i === hi}
              onMouseDown={(e) => { e.preventDefault(); pick(p) }}
              onMouseEnter={() => setHi(i)}
              style={{ minHeight: 52, padding: "8px 10px", borderRadius: 10, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", background: i === hi ? "#EFF4F8" : "transparent" }}>
              <Icon name={p.icon} size={20} color="#5E6675" />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 15, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</span>
                {/* 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
                    99-1-decision: the search-all row carries no kind or count line */}
                {p.count >= 0 && <span style={{ display: "block", fontSize: 13, color: "#5E6675", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.kind}{p.sub ? ` · ${p.sub}` : ""}{p.alias ? ` · matches “${p.alias}”` : ""}</span>}
              </span>
              {p.count >= 0 && <span style={{ fontSize: 13, color: "#5E6675", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{p.count} {p.count === 1 ? "lot" : "lots"}</span>}{/* 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
                99-1-decision: singular for one lot */}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
